import Modal from "react-bootstrap/Modal";
import { Button, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

/**
 * @typedef {"confirm" | "loading" | "success" | "error"} LogoutModalState
 */

/**
 * @typedef {Object} LogoutModalProps
 * @property {boolean} showModal - Controls the visibility of the modal
 * @property {() => void} handleClose - Function to close the modal
 * @property {() => void} onLogout - Function to call when user confirms logout
 * @property {boolean} loading - Whether logout is in progress
 * @property {boolean} success - Whether logout was successful
 * @property {string} [error] - Error message if logout failed
 * @property {string} [redirectPath] - Path to redirect after success (default: "/login")
 */

/**
 * A reusable logout modal component that handles confirmation, loading, success, and error states.
 */
const LogoutModal = ({
  showModal,
  handleClose,
  onLogout,
  loading = false,
  success = false,
  error = "",
  redirectPath = "/login",
}) => {
  const navigate = useNavigate();

  // Determine current state
  const getModalState = () => {
    if (loading) return "loading";
    if (success) return "success";
    if (error) return "error";
    return "confirm";
  };

  const modalState = getModalState();

  // Config for different states
  const stateConfig = {
    confirm: {
      title: "Confirm Logout",
      message:
        "Are you sure you want to logout? You will need to sign in again to access your account.",
      titleClass: "text-dark",
      showPrimaryButton: true,
      showSecondaryButton: true,
      primaryButtonText: "Logout",
      secondaryButtonText: "Cancel",
      onPrimaryClick: onLogout,
      onSecondaryClick: handleClose,
    },
    loading: {
      title: "Logging Out",
      message: "Please wait while we securely log you out...",
      titleClass: "text-primary",
      showPrimaryButton: false,
      showSecondaryButton: false,
    },
    success: {
      title: "Logout Successful",
      message:
        "You have been successfully logged out. Thank you for using YuvaQuest!",
      titleClass: "text-success",
      showPrimaryButton: true,
      showSecondaryButton: false,
      primaryButtonText: "Go to Login",
      onPrimaryClick: () => {
        handleClose();
        navigate(redirectPath);
      },
    },
    error: {
      title: "Logout Failed",
      message:
        error || "Something went wrong while logging out. Please try again.",
      titleClass: "text-danger",
      showPrimaryButton: true,
      showSecondaryButton: true,
      primaryButtonText: "Retry",
      secondaryButtonText: "Cancel",
      onPrimaryClick: onLogout,
      onSecondaryClick: handleClose,
    },
  };

  const config = stateConfig[modalState];

  useEffect(() => {
    if (success) {
      // setTimeout(() => {
      handleClose();
      navigate(redirectPath);
      // }, 2000)
    }
  }, [success]);

  return (
    <Modal
      show={showModal}
      onHide={modalState === "loading" ? undefined : handleClose}
      centered
      className="custom-modal"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Body className="p-2 m-4">
        <div className="text-center mb-2">
          <h5 className={`${config.titleClass} fw-bold fs-3`}>
            {config.title}
          </h5>
        </div>

        {modalState === "loading" && (
          <div className="text-center my-4">
            <Spinner animation="border" variant="primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}

        <p className="text-center mb-4 mt-2 pt-2 custom-modal-description">
          {config.message}
        </p>

        <div
          className={`d-flex flex-column flex-md-row gap-3 ${
            config.showSecondaryButton
              ? "justify-content-center"
              : "justify-content-center align-items-center"
          }`}
        >
          {config.showSecondaryButton && (
            <button
              className="custom-cancel-button"
              onClick={config.onSecondaryClick}
            >
              {config.secondaryButtonText}
            </button>
          )}
          {config.showPrimaryButton && (
            <Button
              className={`custom-primary-button ${!config.showSecondaryButton ? "w-auto" : ""}`}
              onClick={config.onPrimaryClick}
            >
              {config.primaryButtonText}
            </Button>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default LogoutModal;
